import { Popover } from '@base-ui/react/popover';
import { useEffect, useState } from 'react';

import {
  DROPDOWN_MENU_TRIGGER_CLASS_NAME,
  DropdownMenu,
} from '#/components/ui/DropdownMenu';
import { Icon } from '#/components/ui/icons';
import { useRootContainer } from '#/context/RootContainerContext';
import { cn } from '#/utils/cn';

export const FACET_TRIGGER_WIDTH_CLASS_NAMES = {
  sector: 'sm:w-[200px]',
  marketCap: 'sm:w-[200px]',
  category: 'sm:w-[210px]',
  exchange: 'sm:w-[160px]',
} as const;

export const FACET_POPUP_WIDTH_CLASS_NAMES = {
  sector: 'w-(--anchor-width) sm:w-[260px]',
  marketCap: 'w-(--anchor-width) sm:w-[240px]',
  category: 'w-(--anchor-width) sm:w-[240px]',
  exchange: 'w-(--anchor-width) sm:w-[300px]',
} as const;

const FACET_MENU_VIEWPORT_CLASS_NAME =
  'bg-gray-10 max-h-[222px] overflow-auto overscroll-y-contain';
const FACET_POPOVER_SEARCH_INPUT_CLASS_NAME =
  'text-static-14 min-w-0 flex-1 bg-transparent py-6 leading-normal outline-none placeholder:text-slate-50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none';
const CLEAR_BUTTON_CLASS_NAME =
  'mdc-btn mdc-btn--link text-static-12 py-4 text-slate-50 normal-case';
const FACET_POPOVER_CLASS_NAME =
  'bg-gray-10 divide-divider border-divider-subtle overflow-hidden rounded border shadow-(--shadow-card) transition-[opacity,translate] duration-150 ease-[cubic-bezier(0.2,0,0,1)] will-change-transform data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:data-ending-style:-translate-y-4 data-[side=bottom]:data-starting-style:-translate-y-4 data-[side=top]:data-ending-style:translate-y-4 data-[side=top]:data-starting-style:translate-y-4 motion-reduce:transform-none motion-reduce:transition-none';
const FACET_CHECKBOX_BUTTON_CLASS_NAME =
  'text-fg-header rounded-4 hover:bg-gray-20 focus:bg-gray-20 flex w-full cursor-pointer items-center px-12 py-10 text-left text-sm';

export function CountBubble({ count }: { count: number }) {
  return (
    <span className="bg-button-primary-bg text-gray-10 text-static-12 inline-flex h-20 min-w-20 shrink-0 items-center justify-center rounded-full px-4 leading-none font-semibold">
      {count}
    </span>
  );
}

/**
 * Optional GTM annotations forwarded onto a facet trigger button so the
 * delegated click listener attributes the open interaction. Spread onto the
 * underlying Base UI trigger as `data-track` / `data-track-label` /
 * `data-track-context`; absent means the trigger carries no annotation.
 */
export interface FacetTriggerTrackProps {
  'data-track'?: string;
  'data-track-label'?: string;
  'data-track-context'?: string;
}

export function FacetMenu({
  triggerLabel,
  triggerContent,
  triggerClassName,
  triggerTrackProps,
  popupClassName,
  viewportClassName = FACET_MENU_VIEWPORT_CLASS_NAME,
  footerContent,
  popupMatchTrigger = true,
  children,
}: {
  triggerLabel: string;
  triggerContent?: React.ReactNode;
  triggerClassName?: string;
  triggerTrackProps?: FacetTriggerTrackProps;
  popupClassName?: string;
  viewportClassName?: string;
  footerContent?: React.ReactNode;
  popupMatchTrigger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger
        className={triggerClassName}
        aria-label={triggerLabel}
        {...triggerTrackProps}
      >
        {triggerContent ?? (
          <span className="min-w-0 truncate">{triggerLabel}</span>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Positioner
          align="start"
          sideOffset={4}
          collisionAvoidance={{ side: 'none' }}
        >
          <DropdownMenu.Popup
            className={popupClassName}
            matchTrigger={popupMatchTrigger}
          >
            <DropdownMenu.Viewport className={viewportClassName}>
              {children}
            </DropdownMenu.Viewport>
            {footerContent}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function NestedFacetSearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="border-divider-subtle bg-gray-10 shrink-0 border-b p-8">
      <label className="border-divider-subtle focus:border-blue-30 text-fg-header bg-gray-10 flex items-center gap-8 rounded border px-12 shadow-[0_1px_0_rgba(1,63,101,0.04)] focus-within:ring-2 focus-within:ring-blue-50 focus-within:ring-offset-2 focus-within:ring-offset-transparent">
        <span className="sr-only">Search</span>
        <Icon id="search" size={14} className="text-form-border" />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          placeholder={placeholder ?? 'Search'}
          aria-label="Search"
          className={FACET_POPOVER_SEARCH_INPUT_CLASS_NAME}
        />
      </label>
    </div>
  );
}

export function FacetCheckboxMark({
  checked,
  indeterminate = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'border-form-border bg-gray-10 flex size-16 shrink-0 items-center justify-center rounded-[3px] border text-transparent',
        (checked || indeterminate) &&
          'border-button-primary-bg bg-button-primary-bg text-gray-10',
      )}
    >
      {indeterminate ? (
        <span className="h-2 w-8 rounded-full bg-current" />
      ) : checked ? (
        <Icon id="check" size={16} aria-hidden="true" />
      ) : null}
    </span>
  );
}

export function FacetCheckboxButton({
  label,
  checked,
  indeterminate = false,
  onClick,
  nested = false,
  emphasized = false,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onClick: () => void;
  nested?: boolean;
  emphasized?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      onClick={onClick}
      className={cn(
        FACET_CHECKBOX_BUTTON_CLASS_NAME,
        emphasized ? 'gap-8 font-semibold' : 'font-normal',
      )}
    >
      <span className={cn('flex items-center gap-8', nested && 'pl-28')}>
        <FacetCheckboxMark checked={checked} indeterminate={indeterminate} />
        <span>{label}</span>
      </span>
    </button>
  );
}

export function FacetPopoverTrigger({
  label,
  children,
  className,
  trackProps,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  trackProps?: FacetTriggerTrackProps;
}) {
  return (
    <Popover.Trigger
      className={cn(DROPDOWN_MENU_TRIGGER_CLASS_NAME, className)}
      aria-label={label}
      {...trackProps}
    >
      {children}

      <Icon
        id="chevron-right"
        size={12}
        className="text-fg-muted pointer-events-none ml-auto rotate-90"
      />
    </Popover.Trigger>
  );
}

export function FacetPopoverContent({
  popupClassName,
  popupMatchTrigger,
  children,
}: {
  popupClassName?: string;
  popupMatchTrigger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Popover.Popup
      initialFocus={false}
      className={cn(
        FACET_POPOVER_CLASS_NAME,
        popupMatchTrigger && 'w-(--anchor-width)',
        popupClassName,
      )}
    >
      {children}
    </Popover.Popup>
  );
}

export function FacetPopoverViewport({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="bg-gray-10 max-h-chart overflow-y-auto overscroll-y-contain p-8">
      {children}
    </div>
  );
}

export function FacetPopoverFooter({
  onClear,
  onSelectAll,
  onApply,
  applyDisabled = false,
}: {
  onClear: () => void;
  onSelectAll: () => void;
  onApply: () => void;
  applyDisabled?: boolean;
}) {
  return (
    <div className="border-divider-subtle bg-gray-10 flex shrink-0 items-center justify-between gap-12 border-t px-12 py-8">
      <div className="mr-auto flex gap-8">
        <button
          type="button"
          className={CLEAR_BUTTON_CLASS_NAME}
          onClick={onSelectAll}
        >
          Select all
        </button>

        <button
          type="button"
          className={CLEAR_BUTTON_CLASS_NAME}
          onClick={onClear}
        >
          Deselect all
        </button>
      </div>

      <button
        type="button"
        className="mdc-btn mdc-btn--primary mdc-btn--sm"
        onClick={onApply}
        disabled={applyDisabled}
      >
        Apply
      </button>
    </div>
  );
}

export function useSyncedPendingFacetState<T>(
  selectedValue: T,
  selectionKey: string,
) {
  const [pendingValue, setPendingValue] = useState(selectedValue);

  useEffect(() => {
    setPendingValue(selectedValue);
    // selectionKey captures the stable serialized selection state; depending on
    // the source value directly reintroduces sync churn from fresh identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  return [pendingValue, setPendingValue] as const;
}

export function FacetApplyPopover({
  label,
  triggerContent,
  triggerClassName,
  triggerTrackProps,
  popupClassName,
  popupMatchTrigger,
  searchPlaceholder,
  hideSearch = false,
  onClear,
  onSelectAll,
  onApply,
  onClose,
  applyDisabled = false,
  children,
}: {
  label: string;
  triggerContent: React.ReactNode;
  triggerClassName?: string;
  triggerTrackProps?: FacetTriggerTrackProps;
  popupClassName?: string;
  popupMatchTrigger?: boolean;
  searchPlaceholder?: string;
  hideSearch?: boolean;
  onClear: () => void;
  onSelectAll: () => void;
  onApply: () => void;
  onClose: () => void;
  applyDisabled?: boolean;
  children: (args: {
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  }) => React.ReactNode;
}) {
  const rootContainer = useRootContainer();
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <Popover.Root
      modal={false}
      onOpenChange={(open) => {
        if (open) {
          return;
        }

        setSearchQuery('');
        onClose();
      }}
    >
      <FacetPopoverTrigger
        className={triggerClassName}
        label={label}
        trackProps={triggerTrackProps}
      >
        {triggerContent}
      </FacetPopoverTrigger>

      <Popover.Portal container={rootContainer ?? document.body}>
        <Popover.Positioner
          align="start"
          sideOffset={4}
          collisionAvoidance={{ side: 'none' }}
        >
          <FacetPopoverContent
            popupClassName={popupClassName}
            popupMatchTrigger={popupMatchTrigger}
          >
            {hideSearch ? null : (
              <NestedFacetSearchField
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={searchPlaceholder}
              />
            )}

            <FacetPopoverViewport>
              {children({ searchQuery, setSearchQuery })}
            </FacetPopoverViewport>

            <FacetPopoverFooter
              applyDisabled={applyDisabled}
              onClear={() => {
                setSearchQuery('');
                onClear();
              }}
              onApply={() => {
                setSearchQuery('');
                onApply();
              }}
              onSelectAll={() => {
                setSearchQuery('');
                onSelectAll();
              }}
            />
          </FacetPopoverContent>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
